/**
 * Construction et téléchargement du rapport client JSON (export IA).
 */

import {
  getAudits,
  getTechnicalErrors,
  getAIRecommendations,
  getPositioningAnalyses,
  getReferencePageTodos,
} from '../api';
import { CLIENT_REPORT_SCHEMA_VERSION } from '../constants/clientReportBlocks';
import { buildReferenceReportPages, getReferencePagesList, normalizePageUrl } from './referencePages';
import { buildReferencePageExportEntry } from './referencePageExport';
import { buildEnrichedReferencePageReport } from './referencePageReportFormat';
import { prepareReportPagesForExport } from './referencePagesCsvExport';
import { buildSeoUrlsExportTable } from './seoUrlsExport';
import {
  verdictGlobal,
  impactByPillar,
  projectedScore,
} from './reportNarrative';
import { getScoreQualitative } from './scoreLabels';
import {
  extractGeoInsights,
  extractSeoRecommendations,
  geoTextsForDedupe,
} from './syntheseInsights';
import { buildCommonFindings } from './commonFindings';
import { flattenTechnicalErrors, isOperationalTechnicalError } from './technicalErrors';
import { translateError } from './auditDictionary';
const MAX_TECH_ERRORS = 30;
const AI_INSTRUCTIONS = [
  'Rédiger un rapport client en français à partir de ce JSON uniquement.',
  'Citer les scores et chiffres exactement comme fournis.',
  'Prioriser exportOptions.consultantComment pour le contexte humain et les priorités business.',
  'Les champs fullNarrativeForAi et fullTableNarrativeForAi regroupent le contenu détaillé (équivalent exports CSV).',
  'Ne pas inventer de données absentes du fichier.',
].join(' ');

function dateStamp() {
  return new Date().toISOString().split('T')[0];
}

function safeSlug(project) {
  return (project?.slug || project?.name || 'projet').replace(/[^a-z0-9-_]/gi, '-').slice(0, 40);
}

function itemText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  return item.title || item.tache || item.description || item.action || '';
}

function buildReferencePagesSection(project, reportPages, todosByUrl) {
  const projectName = project?.name || project?.slug || '';
  return {
    pages: (reportPages || []).map((page) => {
      const norm = normalizePageUrl(page.resolvedUrl || page.url);
      const altNorm = normalizePageUrl(page.url);
      const todo = todosByUrl[norm] || todosByUrl[altNorm] || todosByUrl[page.url] || null;
      const entry = buildReferencePageExportEntry(
        {
          url: page.url,
          title: page.title,
          meta_description: page.meta_description,
        },
        page.seo,
        page.perf,
        page.errors,
        todo,
      );
      return buildEnrichedReferencePageReport(entry, projectName);
    }),
  };
}

function buildTechnicalSection(errorRows) {
  const sorted = [...(errorRows || [])]
    .filter((e) => !isOperationalTechnicalError(e))
    .sort((a, b) => {
      const sev = { high: 0, medium: 1, low: 2 };
      return (sev[a.severity] ?? 2) - (sev[b.severity] ?? 2);
    })
    .slice(0, MAX_TECH_ERRORS);

  return {
    errors: sorted.map((err) => {
      const t = translateError(err);
      return {
        category: err.category || '',
        severity: err.severity || 'medium',
        impact: t?.pourquoi || err.impact || '',
        effort: t?.effort || err.effort || 'variable',
        message: t?.label || err.message || '',
        pagesCount: err.count || err.pages?.length || 1,
        exampleUrls: (err.pages || [err.url]).filter(Boolean).slice(0, 3),
      };
    }),
  };
}

function buildGeoSection(positioning) {
  const geo = extractGeoInsights(positioning);
  return {
    scoreGlobalGeo: geo.scoreGlobalGeo,
    pointsForts: (geo.pointsForts || []).map(itemText),
    pointsFaibles: (geo.pointsFaibles || []).map(itemText),
    recommendations: (geo.todos || []).map((t) => ({
      title: itemText(t),
      description: typeof t === 'object' ? (t.description || '') : '',
    })),
    alertes: (geo.alertes || []).map((a) => ({
      title: itemText(a),
      description: typeof a === 'object' ? (a.description || '') : '',
    })),
  };
}

function computeAuditCoveragePercent(km) {
  const pagesSitemap = km.pages_sitemap ?? 0;
  const nbAudits = km.nb_audits_seo ?? 0;
  if (pagesSitemap <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((nbAudits / pagesSitemap) * 100)));
}

function buildSiteIdentitySection(project, syntheseData) {
  const km = syntheseData?.key_metrics || {};
  const ti = syntheseData?.technical_info || {};
  const sr = syntheseData?.site_recap || {};
  const coveragePercent = computeAuditCoveragePercent(km);
  const llmFile = project?.has_llm_file ?? ti.has_llm_file ?? null;

  return {
    profile: {
      secteur: sr.secteur || '',
      comprehensionIa: sr.comprehension_ia || '',
      description: sr.description || project?.description || '',
    },
    technologies: {
      cms: ti.cms || '',
      hosting: ti.hosting || '',
      technology: ti.technology || ti.framework || '',
      framework: ti.framework || '',
      marketingTools: ti.marketing_tools || '',
      cdn: ti.cdn || '',
      server: ti.server || '',
      isMultilingual: Boolean(ti.is_multilingual),
      hreflangLanguages: ti.hreflang_languages || '',
    },
    auditCoverage: {
      llmTxt: llmFile === null ? null : (llmFile ? 'present' : 'absent'),
      sitemap: {
        missing: Boolean(km.sitemap_missing),
        pagesCount: km.pages_sitemap ?? 0,
        label: km.sitemap_missing ? 'Manquant' : `${km.pages_sitemap ?? 0} pages`,
      },
      auditedUrlsCount: km.nb_audits_seo ?? 0,
      auditedUrlsPercent: coveragePercent,
      auditedUrlsPercentLabel: coveragePercent === null
        ? null
        : `${coveragePercent}% des pages du sitemap auditées`,
      urlDiscoverySource: km.url_discovery_source || null,
      sitemapNotice: km.sitemap_notice || project?.sitemap_notice || null,
      scorePenaltyPoints: km.score_penalty_points || project?.score_penalty_points || 0,
    },
  };
}

function buildSeoInsightsSection(aiRecommendations, positioning) {
  const geo = extractGeoInsights(positioning);
  const seo = extractSeoRecommendations(aiRecommendations, geoTextsForDedupe(geo));
  return {
    summary: seo.summary,
    priorityRecommendations: (seo.priorityRecos || []).map((r) => ({
      title: itemText(r),
      why: typeof r === 'object' ? (r.pourquoi || r.why || r.description || '') : '',
      effort: typeof r === 'object' ? (r.effort || '') : '',
      axe: typeof r === 'object' ? (r.axe || r.category || '') : '',
    })),
  };
}

/**
 * @param {object} project
 * @param {object} context
 * @param {object} context.syntheseData
 * @param {string[]} enabledBlockIds
 * @param {string} consultantComment
 */
export function buildClientReportJson(project, context, { enabledBlockIds, consultantComment = '' }) {
  const enabled = new Set(enabledBlockIds || []);
  const enabledList = [...enabled];
  const km = context.syntheseData?.key_metrics || {};
  const ti = context.syntheseData?.technical_info || {};
  const sr = context.syntheseData?.site_recap || {};

  const pillars = {
    seo: Math.round(Number(project?.score_seo) || km.score_seo_moyen || 0),
    performance: Math.round(Number(project?.score_performance) || km.score_performance_moyen || 0),
    technique: Math.round(Number(project?.score_technique) || km.score_technique_moyen || 0),
  };
  const global = Math.round(Number(project?.score_global) || km.score_global_moyen || 0);

  const out = {
    schemaVersion: CLIENT_REPORT_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    exportOptions: {
      enabledBlocks: enabledList.filter((id) => id !== 'meta'),
      consultantComment: consultantComment || '',
    },
    meta: {
      instructionsForAI: AI_INSTRUCTIONS,
      projectName: project?.name || project?.slug || '',
      slug: project?.slug || '',
      url: project?.url || '',
      auditedAt: project?.date_dernier_audit || null,
      exportDate: dateStamp(),
      scope: {
        multilingual: Boolean(ti.is_multilingual),
        primaryLanguage: ti.hreflang_languages || null,
        urlsAuditedCount: km.nb_audits_seo ?? context.audits?.length ?? 0,
        referencePagesCount: getReferencePagesList(project).length,
        sector: sr.secteur || '',
      },
    },
  };

  if (enabled.has('scores')) {
    const qGlobal = getScoreQualitative(global);
    out.scores = {
      global: global > 0 ? global : null,
      seo: pillars.seo > 0 ? pillars.seo : null,
      performance: pillars.performance > 0 ? pillars.performance : null,
      technique: pillars.technique > 0 ? pillars.technique : null,
      labels: {
        global: global > 0 ? qGlobal.label : null,
        seo: pillars.seo > 0 ? getScoreQualitative(pillars.seo).label : null,
        performance: pillars.performance > 0 ? getScoreQualitative(pillars.performance).label : null,
        technique: pillars.technique > 0 ? getScoreQualitative(pillars.technique).label : null,
      },
    };
  }

  if (enabled.has('siteIdentity')) {
    out.siteIdentity = buildSiteIdentitySection(project, context.syntheseData);
  }

  if (enabled.has('narrative')) {
    const projection = projectedScore(pillars);
    out.narrative = {
      verdictGlobal: verdictGlobal(global, pillars),
      pillarImpacts: impactByPillar(pillars),
      projectedScore: projection
        ? {
          min: projection.min,
          max: projection.max,
          gain: projection.gain,
          disclaimer: 'Projection indicative si les piliers faibles sont améliorés ; non contractuelle.',
        }
        : null,
    };
  }

  if (enabled.has('geo') && context.positioning) {
    out.geo = buildGeoSection(context.positioning);
  }

  if (enabled.has('seoInsights') && context.aiRecommendations) {
    out.seoInsights = buildSeoInsightsSection(context.aiRecommendations, context.positioning);
  }

  if (enabled.has('technical') && context.errorRows?.length) {
    out.technical = buildTechnicalSection(context.errorRows);
  }

  if (enabled.has('commonFindings') && context.reportPages?.length) {
    const cf = buildCommonFindings(context.reportPages);
    out.commonFindings = {
      totalPages: cf.totalPages,
      systemic: (cf.systemic || []).map((f) => ({
        key: f.key,
        label: f.label,
        why: f.pourquoi,
        corriger: f.corriger,
        effort: f.effort,
        nbPages: f.nbPages,
        severity: f.severity,
      })),
    };
  }

  if (enabled.has('referencePages') && context.reportPages?.length) {
    out.referencePages = buildReferencePagesSection(
      project,
      context.reportPages,
      context.todosByUrl || {},
    );
  }

  if (enabled.has('seoUrlsTable') && context.audits?.length) {
    out.seoUrlsTable = buildSeoUrlsExportTable(project, context.audits);
  }

  return out;
}

/**
 * Charge les données nécessaires selon les blocs cochés.
 */
export async function fetchClientReportContext(slug, project, enabledBlockIds) {
  const enabled = new Set(enabledBlockIds || []);
  const needsAudits = enabled.has('seoUrlsTable') || enabled.has('referencePages');
  const needsErrors = enabled.has('technical') || enabled.has('referencePages');
  const needsRef = enabled.has('referencePages') || enabled.has('commonFindings');
  const needsAi = enabled.has('seoInsights');
  const needsGeo = enabled.has('geo') || enabled.has('seoInsights');
  const needsTodos = enabled.has('referencePages');

  const [audits, errorsData, aiRecommendations, positioningList, todosMap] = await Promise.all([
    needsAudits || needsRef ? getAudits(slug).catch(() => []) : Promise.resolve([]),
    needsErrors || needsRef ? getTechnicalErrors(slug).catch(() => null) : Promise.resolve(null),
    needsAi ? getAIRecommendations(slug).catch(() => null) : Promise.resolve(null),
    needsGeo ? getPositioningAnalyses(slug).catch(() => []) : Promise.resolve([]),
    needsTodos ? getReferencePageTodos(slug).catch(() => ({})) : Promise.resolve({}),
  ]);

  let reportPages = [];
  if (needsRef) {
    reportPages = await buildReferenceReportPages(slug, project);
    if (enabled.has('referencePages')) {
      reportPages = await prepareReportPagesForExport(slug, project, reportPages);
    }
  }

  const errorRows = flattenTechnicalErrors(errorsData, project);

  return {
    audits,
    errorRows,
    aiRecommendations,
    positioning: positioningList?.[0] || null,
    reportPages,
    todosByUrl: todosMap || {},
  };
}

function downloadJsonBlob(filename, data) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * @param {string} slug
 * @param {object} project
 * @param {object} options
 * @param {object} options.syntheseData
 * @param {string[]} options.enabledBlockIds
 * @param {string} options.consultantComment
 */
export async function downloadClientReportJson(slug, project, options = {}) {
  const { syntheseData, enabledBlockIds, consultantComment } = options;
  const enabled = new Set(enabledBlockIds || []);
  enabled.add('meta');

  const context = await fetchClientReportContext(slug, project, [...enabled]);
  context.syntheseData = syntheseData;

  const payload = buildClientReportJson(project, context, {
    enabledBlockIds: [...enabled],
    consultantComment,
  });

  const filename = `rapport-client-${safeSlug(project)}-${dateStamp()}.json`;
  downloadJsonBlob(filename, payload);
  return payload;
}

/** Indique si un bloc a des données exportables (pour griser la case). */
export function blockAvailability(blockId, { project, syntheseData, auditsCount }) {
  const refCount = getReferencePagesList(project).length;
  const km = syntheseData?.key_metrics || {};

  switch (blockId) {
    case 'meta':
      return { available: true, reason: '' };
    case 'scores':
      return {
        available: (project?.score_global || km.score_global_moyen) > 0,
        reason: 'Aucun score calculé',
      };
    case 'narrative':
      return {
        available: (project?.score_global || km.score_global_moyen) > 0,
        reason: 'Verdict indisponible sans score',
      };
    case 'siteIdentity': {
      const sr = syntheseData?.site_recap || {};
      const ti = syntheseData?.technical_info || {};
      const hasProfile = Boolean(sr.secteur || sr.comprehension_ia || sr.description || project?.description);
      const hasTech = Boolean(
        ti.cms || ti.hosting || ti.technology || ti.framework || ti.marketing_tools || ti.is_multilingual,
      );
      const hasCoverage = (km.pages_sitemap ?? 0) > 0
        || project?.has_llm_file != null
        || ti.has_llm_file != null
        || km.sitemap_missing;
      return {
        available: hasProfile || hasTech || hasCoverage,
        reason: 'Profil et technologies non renseignés (lancez « Extraire » sur la synthèse)',
      };
    }
    case 'geo':
      return {
        available: Boolean(syntheseData?.positioning || syntheseData?.analysis),
        reason: 'Pas d’analyse de positionnement',
      };
    case 'seoInsights':
      return { available: true, reason: '' };
    case 'technical':
      return { available: true, reason: '' };
    case 'commonFindings':
      return {
        available: refCount > 0,
        reason: 'Aucune page de référence',
      };
    case 'referencePages':
      return {
        available: refCount > 0,
        reason: 'Aucune page de référence',
      };
    case 'seoUrlsTable':
      return {
        available: (auditsCount ?? km.nb_audits_seo) > 0,
        reason: 'Aucun audit SEO',
      };
    default:
      return { available: true, reason: '' };
  }
}
